import { useEffect, useMemo, useState } from 'react'
import { BookMarked, Bookmark, Eye, ExternalLink, Search, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthSession } from '../../lib/firebase'
import { getSections, getStations, type ApiPayloadResult } from '../../lib/api'
import { learnerStationLabel } from '../../lib/learnerLabels'
import { PROGRESS_STORAGE_EVENT } from '../../lib/progress'
import type { Section, Station, StationProgress } from '../../lib/types'
import { readProgressStore, readProgressStoreForFallback } from './progress'
import styles from './review.module.css'

type ReviewTab = 'review' | 'bookmarks'

export interface ReviewRouteProps {
  /** When omitted, the route still renders from the progress store and can be fed data by the app shell later. */
  stations?: Station[]
  sections?: Section[]
  uid?: string
}

interface ReviewItem {
  station: Station
  sectionId: string
  sectionLabel: string
  progress: StationProgress
  available: boolean
}

interface ReviewContentState {
  status: 'loading' | 'ready' | 'error'
  stations: Station[]
  sections: Section[]
  warning?: string
  error?: string
}

function createUnavailableStation(stationId: string): Station {
  return {
    id: stationId,
    sectionId: 'unknown',
    title: `محطة لم تعد متاحة (${stationId})`,
    type: 'mixed',
    status: 'archived',
    order: Number.MAX_SAFE_INTEGER,
    images: [],
    subquestions: [],
  }
}

function isTrackedProgress(progress: StationProgress | undefined): progress is StationProgress {
  return Boolean(progress && (progress.mark === 'review' || progress.bookmarked))
}

function normalizeSearch(value: string): string {
  return value.trim().toLocaleLowerCase('ar')
}

function getSearchText(item: ReviewItem): string {
  return [
    item.station.title,
    item.station.scenario ?? '',
    item.station.source ?? '',
    item.sectionLabel,
    ...(item.station.tags ?? []),
  ]
    .filter((part): part is string => typeof part === 'string')
    .join(' ')
    .toLocaleLowerCase('ar')
}

function formatProgressDate(value: string | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('ar-EG')
}

function useProgressRevision(uid: string): number {
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const bumpIfRelevant = (event: Event) => {
      const detail = (event as CustomEvent<{ uid?: unknown }>).detail
      if (!detail?.uid || detail.uid === uid) setRevision((value) => value + 1)
    }

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === `ospe-progress:${uid}`) {
        setRevision((value) => value + 1)
      }
    }

    window.addEventListener(PROGRESS_STORAGE_EVENT, bumpIfRelevant)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(PROGRESS_STORAGE_EVENT, bumpIfRelevant)
      window.removeEventListener('storage', handleStorage)
    }
  }, [uid])

  return revision
}

function useReviewContent(stationsProp: Station[] | undefined, sectionsProp: Section[] | undefined): ReviewContentState {
  const isControlled = stationsProp !== undefined && sectionsProp !== undefined
  const [state, setState] = useState<ReviewContentState>(() => (
    isControlled
      ? { status: 'ready', stations: stationsProp ?? [], sections: sectionsProp ?? [] }
      : { status: 'loading', stations: [], sections: [] }
  ))

  useEffect(() => {
    let cancelled = false

    if (isControlled) {
      setState({ status: 'ready', stations: stationsProp ?? [], sections: sectionsProp ?? [] })
      return () => {
        cancelled = true
      }
    }

    const sectionsRequest: Promise<ApiPayloadResult<Section[]>> = sectionsProp === undefined
      ? getSections()
      : Promise.resolve({ data: sectionsProp, source: 'api' as const } as ApiPayloadResult<Section[]>)
    const stationsRequest: Promise<ApiPayloadResult<Station[]>> = stationsProp === undefined
      ? getStations()
      : Promise.resolve({ data: stationsProp, source: 'api' as const } as ApiPayloadResult<Station[]>)

    Promise.all([sectionsRequest, stationsRequest])
      .then(([sectionsResult, stationsResult]) => {
        if (cancelled) return
        setState({
          status: 'ready',
          sections: sectionsResult.data,
          stations: stationsResult.data,
          warning: sectionsResult.warning ?? stationsResult.warning,
        })
      })
      .catch((reason: unknown) => {
        if (cancelled) return
        setState({
          status: 'error',
          sections: [],
          stations: [],
          error: reason instanceof Error ? reason.message : 'تعذر تحميل محطات المراجعة.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [isControlled, sectionsProp, stationsProp])

  return state
}

function useReviewRows(stations: Station[], sections: Section[], uid: string, revision: number): ReviewItem[] {
  const progress = useMemo(
    () => readProgressStore(uid) ?? readProgressStoreForFallback(uid),
    [revision, uid],
  )

  return useMemo(() => {
    const sectionById = new Map(sections.map((section) => [section.id, section]))
    const stationById = new Map(stations.map((station) => [station.id, station]))
    const rows: ReviewItem[] = []

    for (const [stationId, stationProgress] of Object.entries(progress.stations)) {
      if (!isTrackedProgress(stationProgress)) continue

      const station = stationById.get(stationId)
      if (station) {
        const sectionId = station.sectionId || 'unknown'
        rows.push({
          station,
          sectionId,
          sectionLabel: sectionById.get(sectionId)?.name ?? 'قسم غير محدد',
          progress: stationProgress,
          available: true,
        })
      } else {
        rows.push({
          station: createUnavailableStation(stationId),
          sectionId: 'unknown',
          sectionLabel: 'محتوى لم يعد متاحًا',
          progress: stationProgress,
          available: false,
        })
      }
    }

    const sectionOrder = new Map(sections.map((section) => [section.id, section.order]))
    rows.sort((left, right) => {
      const sectionDelta = (sectionOrder.get(left.sectionId) ?? Number.MAX_SAFE_INTEGER)
        - (sectionOrder.get(right.sectionId) ?? Number.MAX_SAFE_INTEGER)
      if (sectionDelta !== 0) return sectionDelta
      const stationDelta = left.station.order - right.station.order
      if (stationDelta !== 0) return stationDelta
      return left.station.title.localeCompare(right.station.title, 'ar')
    })

    return rows
  }, [progress, sections, stations])
}

function ReviewCard({ item }: { item: ReviewItem }) {
  const markLabel = item.progress.mark === 'review' ? 'تحتاج مراجعة' : 'غير مقَيّمة'
  const image = item.station.images[0]
  const updatedDate = formatProgressDate(item.progress.answeredAt)

  return (
    <li className={`${styles.reviewCard}${item.available ? '' : ` ${styles.reviewCardUnavailable}`}`}>
      <div className={styles.reviewCardHeader}>
        {item.available ? (
          <Link
            to={`/stations/${encodeURIComponent(item.station.id)}`}
            className={styles.reviewCardTitleLink}
            aria-label={`فتح ${learnerStationLabel(item.station.id, item.station.order)}`}
          >
            <div className={styles.reviewCardTitleWrap}>
              <h2>{learnerStationLabel(item.station.id, item.station.order)}</h2>
              <p>{item.sectionLabel}</p>
            </div>
            <ExternalLink size={16} aria-hidden="true" />
          </Link>
        ) : (
          <div className={styles.reviewCardTitleLink} aria-label="محطة محفوظة غير متاحة">
            <div className={styles.reviewCardTitleWrap}>
              <h2>{learnerStationLabel(item.station.id, item.station.order)}</h2>
              <p>{item.sectionLabel}</p>
            </div>
          </div>
        )}

        <div className={styles.reviewCardMeta} aria-label="حالة المحطة">
          {item.progress.mark === 'review' && <span className={styles.reviewCardBadge}>{markLabel}</span>}
          <span className={styles.reviewCardBadge}>
            {item.progress.bookmarked ? (
              <>
                <BookMarked size={14} aria-hidden="true" />
                محفوظة
              </>
            ) : (
              <>
                <Eye size={14} aria-hidden="true" />
                ضمن قائمة المراجعة
              </>
            )}
          </span>
        </div>
      </div>

      {image && item.available && (
        <img
          src={image.key}
          alt={`صورة ${learnerStationLabel(item.station.id, item.station.order)}`}
          className={styles.reviewCardImage}
          loading="lazy"
        />
      )}

      <p className={styles.reviewCardDescription}>
        {item.available
          ? (item.station.scenario?.trim() || 'لم يُضف سيناريو لهذه المحطة بعد.')
          : 'لم تعد هذه المحطة متاحة ضمن المحتوى الحالي.'}
      </p>
      <p className={styles.reviewCardSource}>
        <span>{item.station.subquestions.length} سؤال فرعي</span>
        {updatedDate && <span>آخر مراجعة: {updatedDate}</span>}
      </p>
    </li>
  )
}

export function ReviewRoute({ stations: stationsProp, sections: sectionsProp, uid }: ReviewRouteProps = {}) {
  const session = useAuthSession()
  const progressUid = uid?.trim() || session.user?.uid || 'demo-user'
  const content = useReviewContent(stationsProp, sectionsProp)
  const revision = useProgressRevision(progressUid)
  const rows = useReviewRows(content.stations, content.sections, progressUid, revision)
  const [tab, setTab] = useState<ReviewTab>('review')
  const [query, setQuery] = useState('')
  const [sectionFilter, setSectionFilter] = useState('all')

  const sectionOptions = useMemo(() => {
    const options: Array<{ id: string; name: string }> = [{ id: 'all', name: 'كل الأقسام' }]
    const seen = new Set<string>()
    for (const section of content.sections) {
      if (!section.id || seen.has(section.id)) continue
      seen.add(section.id)
      options.push({ id: section.id, name: section.name })
    }
    if (rows.some((row) => row.sectionId === 'unknown')) options.push({ id: 'unknown', name: 'محتوى لم يعد متاحًا' })
    return options
  }, [content.sections, rows])

  const visibleRows = useMemo(() => {
    const normalizedQuery = normalizeSearch(query)
    return rows.filter((row) => {
      if (tab === 'review' && row.progress.mark !== 'review') return false
      if (tab === 'bookmarks' && !row.progress.bookmarked) return false
      if (sectionFilter !== 'all' && row.sectionId !== sectionFilter) return false
      return normalizedQuery.length === 0 || getSearchText(row).includes(normalizedQuery)
    })
  }, [query, rows, sectionFilter, tab])

  const reviewCount = rows.filter((row) => row.progress.mark === 'review').length
  const bookmarkCount = rows.filter((row) => row.progress.bookmarked).length
  const hasFilters = query.trim().length > 0 || sectionFilter !== 'all'

  function clearFilters() {
    setQuery('')
    setSectionFilter('all')
  }

  if (content.status === 'loading') {
    return (
      <main className={styles.reviewPage} dir="rtl" aria-live="polite">
        <p className={styles.reviewKicker}>قائمتي</p>
        <h1 className={styles.reviewTitle}>جارٍ تجهيز قائمتك</h1>
        <p className={styles.reviewDescription}>جارٍ تحميل المحطات وربطها بتقدمك المحفوظ.</p>
      </main>
    )
  }

  if (content.status === 'error') {
    return (
      <main className={styles.reviewPage} dir="rtl" aria-live="polite">
        <p className={styles.reviewKicker}>قائمتي</p>
        <h1 className={styles.reviewTitle}>تعذر تحميل قائمتك</h1>
        <p className={styles.reviewDescription}>{content.error}</p>
      </main>
    )
  }

  return (
    <main className={styles.reviewPage} dir="rtl" aria-label="صفحة المراجعة">
      <header className={styles.reviewHeader}>
        <p className={styles.reviewKicker}>قائمتي</p>
        <h1 className={styles.reviewTitle}>المحطات التي تهمك</h1>
        <p className={styles.reviewDescription}>
          ستجد هنا المحطات التي تحتاج إلى مراجعتها، والمحطات التي حفظتها للرجوع إليها لاحقًا.
        </p>
      </header>

      <div role="tablist" aria-label="تبديل عرض المراجعة" className={styles.tabs}>
        <button
          id="review-tab"
          type="button"
          role="tab"
          aria-selected={tab === 'review'}
          aria-controls="review-panel"
          className={tab === 'review' ? styles.tabActive : styles.tab}
          onClick={() => setTab('review')}
        >
          تحتاج مراجعة
          <span className={styles.badge}>{reviewCount}</span>
        </button>
        <button
          id="bookmark-tab"
          type="button"
          role="tab"
          aria-selected={tab === 'bookmarks'}
          aria-controls="review-panel"
          className={tab === 'bookmarks' ? styles.tabActive : styles.tab}
          onClick={() => setTab('bookmarks')}
        >
          المحفوظة
          <span className={styles.badge}>{bookmarkCount}</span>
        </button>
      </div>

      <section className={styles.filters} aria-label="عوامل التصفية">
        <label className={styles.searchField}>
          <span className={styles.searchLabel}>البحث</span>
          <span className={styles.searchControl}>
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث بالعنوان أو القسم أو المحتوى…"
              aria-label="ابحث في قوائم المراجعة"
            />
            {query && (
              <button type="button" className={styles.clearSearch} onClick={() => setQuery('')} aria-label="مسح البحث">
                <X size={15} aria-hidden="true" />
              </button>
            )}
          </span>
        </label>

        <label className={styles.selectField}>
          <span>القسم</span>
          <select value={sectionFilter} onChange={(event) => setSectionFilter(event.target.value)} aria-label="التصفية حسب القسم">
            {sectionOptions.map((section) => (
              <option key={section.id} value={section.id}>{section.name}</option>
            ))}
          </select>
        </label>
      </section>

      {content.warning && <p className={styles.contentWarning} role="status">{content.warning}</p>}

      <section
        id="review-panel"
        role="tabpanel"
        aria-labelledby={tab === 'review' ? 'review-tab' : 'bookmark-tab'}
        className={styles.panel}
      >
        <div className={styles.resultSummary} aria-live="polite">
          <span>{visibleRows.length} محطة</span>
          {hasFilters && (
            <button type="button" className={styles.clearFilters} onClick={clearFilters}>
              <X size={14} aria-hidden="true" />
              مسح عوامل التصفية
            </button>
          )}
        </div>

        {visibleRows.length === 0 ? (
          <div className={styles.emptyState} role="status">
            <Bookmark size={22} aria-hidden="true" />
            <p className={styles.emptyStateTitle}>{hasFilters ? 'لا توجد نتائج مطابقة' : 'قائمتك فارغة حاليًا'}</p>
            <p>
              {hasFilters
                ? 'غيّر عبارة البحث أو اختر قسمًا آخر.'
                : tab === 'review'
                  ? 'من شاشة أي محطة اختر «تحتاج مراجعة» لتظهر هنا.'
                  : 'من شاشة أي محطة اختر «حفظ» لتظهر هنا.'}
            </p>
            {hasFilters && <button type="button" onClick={clearFilters}>إظهار كل المحطات</button>}
          </div>
        ) : (
          <ul className={styles.reviewList} aria-label={tab === 'review' ? 'المحطات التي تحتاج مراجعة' : 'المحطات المحفوظة'}>
            {visibleRows.map((item) => <ReviewCard key={item.station.id} item={item} />)}
          </ul>
        )}
      </section>
    </main>
  )
}

export { ReviewRoute as default }
